import React from "react";
import { testIdProps } from "../../../../../utils/testIdProps";
import { useEffect, useRef, useState } from "react";
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/bootstrap.css";
import ErrorMessage from "../../../../../Shared/error-message/errorMessage";
import Styles from "../../Shared/sharedStyles.module.scss";
import {
  buttonStyles,
  StyledButton,
} from "../../../../../Shared/button/button";
import InputField from "../../Shared/inputField/inputField";
import inputcss from "../../Shared/inputField/inputField.module.scss";
import { IdsGenerator } from "../../../../../utils/idGenerator";
import {
  COORDINATE_MAX,
  COORDINATE_MIN,
  defaultESignErrorState,
  SIGNER_EMAIL_MAX_LENGTH,
  SIGNER_NAME_MAX_LENGTH,
  SIGNER_NAME_MIN_LENGTH,
  signTypeBadgeMap,
} from "./esign.constants";
import {
  ESIGN_SIGN_TYPE_OPTIONS,
  SIGN_TYPE,
  SignTypeValue,
} from "./esign.enums";
import {
  hasMeaningfulPhoneInput,
  normalizePhone,
  validateESignFormInput,
} from "./esign.validation";

function getRefValue(ref: React.RefObject<any>): string {
  return String(ref?.current?.value ?? "").trim();
}

function ESignPopup({ handleResponse, name }: any) {
  const documentInputId = IdsGenerator({
    prefix: "esign popup",
    id: "input",
    suffix: "document",
  });
  const [base64, setBase64] = useState<string>("");
  const [selectedFileName, setSelectedFileName] = useState<string>("No file chosen");
  const [signerPhoneValue, setSignerPhoneValue] = useState<string>("");
  const [statex, setStatex] = useState<string>("0");
  const [statey, setStatey] = useState<string>("0");
  const [signType, setSignType] = useState<SignTypeValue>(SIGN_TYPE.EMAIL);

  const signerNameRef = useRef<any>("");
  const signerEmailRef = useRef<any>("");

  const [error, setError] = useState({ ...defaultESignErrorState });
  const [isChecked, setIsChecked] = useState(false);
  const [isDisabled, setIsDisabled] = useState(true);
  const [loading, setLoading] = useState(false);
  const selectedBadge = signTypeBadgeMap[signType];

  useEffect(() => {
    if (signerEmailRef.current) signerEmailRef.current.value = "";
    setSignerPhoneValue("");
    setError({ ...defaultESignErrorState });
  }, [signType]);

  function isValid(
    field?: "name" | "email" | "phone" | "variableX" | "variableY",
    forceShowAllErrors = false,
    phoneValueOverride?: string
  ) {
    const phoneValue =
      typeof phoneValueOverride === "string"
        ? phoneValueOverride.trim()
        : signerPhoneValue.trim();

    const validationResult = validateESignFormInput({
      signType,
      signerName: getRefValue(signerNameRef),
      signerEmail: getRefValue(signerEmailRef),
      signerWhatsappNumber: phoneValue,
      statex,
      statey,
    });

    if (forceShowAllErrors || !field) {
      setError((prev) => ({
        ...defaultESignErrorState,
        ...validationResult.error,
        consentError: prev.consentError,
      }));
      setIsDisabled(!validationResult.isValid);
      return;
    }

    setError((prev) => ({
      ...prev,
      nameError: field === "name" ? validationResult.error.nameError : prev.nameError,
      emailError:
        field === "email" ? validationResult.error.emailError : prev.emailError,
      phoneError:
        field === "phone"
          ? signType === SIGN_TYPE.AADHAAR_OTP && !hasMeaningfulPhoneInput(phoneValue)
            ? false
            : validationResult.error.phoneError
          : prev.phoneError,
      variableXError:
        field === "variableX"
          ? validationResult.error.variableXError
          : prev.variableXError,
      variableYError:
        field === "variableY"
          ? validationResult.error.variableYError
          : prev.variableYError,
      contactEitherOrError:
        field === "email" || field === "phone"
          ? validationResult.error.contactEitherOrError
          : prev.contactEitherOrError,
    }));
    setIsDisabled(!validationResult.isValid);
  }

  function handleData() {
    if (!isChecked) {
      setError((prev) => ({
        ...prev,
        consentError: true,
      }));
      return false;
    }

    const signer_name = getRefValue(signerNameRef);
    const signer_email = getRefValue(signerEmailRef);
    const signer_whatsapp_number = signerPhoneValue.trim();

    const validationResult = validateESignFormInput({
      signType,
      signerName: signer_name,
      signerEmail: signer_email,
      signerWhatsappNumber: signer_whatsapp_number,
      statex,
      statey,
    });
    if (!validationResult.isValid) {
      setError(validationResult.error);
      return false;
    }

    setLoading(true);
    const signerBase: Record<string, unknown> = {
      signer_name,
      signer_purpose: "Demo",
      sign_coordinates: [
        {
          page_num: 0,
          x_coord: Number(statex),
          y_coord: Number(statey),
        },
      ],
    };

    if (signType === SIGN_TYPE.EMAIL) {
      signerBase.signer_email = signer_email;
    } else if (signType === SIGN_TYPE.WHATSAPP) {
      signerBase.signer_whatsapp_number = normalizePhone(signer_whatsapp_number);
    } else {
      if (signer_email.length > 0) signerBase.signer_email = signer_email;
      if (hasMeaningfulPhoneInput(signer_whatsapp_number)) {
        signerBase.signer_whatsapp_number = normalizePhone(signer_whatsapp_number);
      }
    }

    const esignRequest = {
      esign_type: signType,
      document: {
        data: base64,
        info: "Bad Habits",
      },
      signers: [signerBase],
      txn_expiry_min: "10080",
      response_url: "https://webhook.site/e7d48f25-1a26-4c7b-8019-da4952a3cafe",
    };
    setLoading(true);
    handleResponse({ ...esignRequest }, setLoading);
  }

  const uploadImage = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) {
      setSelectedFileName("No file chosen");
      return;
    }

    if (file.type !== "application/pdf") {
      alert("Only PDF files are allowed");
      e.target.value = "";
      setSelectedFileName("No file chosen");
      return;
    }
    setSelectedFileName(file.name);

    let baseValue64 = (await convertBase64(file)) as string;

    const char = ",";
    const index = baseValue64.indexOf(char);

    if (index !== -1) {
      baseValue64 = baseValue64.substring(index + 1);
    }
    setBase64(baseValue64);
  };

  const convertBase64 = (file: Blob) => {
    return new Promise((resolve, reject) => {
      const fileReader = new FileReader();
      fileReader.readAsDataURL(file);

      fileReader.onload = () => {
        resolve(fileReader.result as string);
      };

      fileReader.onerror = (err) => {
        reject(err);
      };
    });
  };

  return (
    <div className={`${Styles.content}`}>
      <div className={`${Styles.infoForm} pl-2`}>
        <hr />
        <div className="d-flex flex-column">
          <div className="app">
            <div className="d-flex flex-column mb-4 ">
              <label className={`${Styles.label} flex-grow-1`}>Document:</label>
              <input
                id={documentInputId} data-testid={documentInputId}
                type="file"
                name="document"
                accept="application/pdf"
                style={{ display: "none" }}
                onChange={(event) => {
                  uploadImage(event);
                }}
              />
              <div className="d-flex align-items-center" style={{ gap: "12px" }}>
                <label
                  htmlFor={documentInputId}
                  style={{
                    border: "1px solid #b6bcc8",
                    borderRadius: "10px",
                    background: "#ffffff",
                    padding: "7px 16px",
                    margin: 0,
                    fontWeight: 600,
                    cursor: "pointer",
                    color: "#1f2937",
                    fontSize: "14px",
                    lineHeight: 1.1,
                  }}
                >
                  Choose file
                </label>
                <span style={{ color: "#6b7280", fontWeight: 500, fontSize: "14px" }}>
                  {selectedFileName}
                </span>
              </div>
            </div>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(2, minmax(280px, 1fr))",
                gap: "16px",
                marginBottom: "16px",
              }}
            >
              <div style={{ minWidth: 0 }}>
                <label className={`${Styles.label} flex-grow-1`}>
                  Signer Name:
                </label>
                <InputField
                  id="id-input-voter"
                  text="lowerCase"
                  onChange={() => isValid("name")}
                  inputRef={signerNameRef}
                  isError={error.nameError}
                  purpose="forVoter"
                  placeholder="Enter signer name"
                />
                {error.nameError && (
                  <ErrorMessage>
                    Signer Name must be {SIGNER_NAME_MIN_LENGTH}-
                    {SIGNER_NAME_MAX_LENGTH} letters (alphabets and spaces only)
                  </ErrorMessage>
                )}
              </div>
              <div style={{ minWidth: 0 }}>
                <label className={`${Styles.label} flex-grow-1`}>Sign Type:</label>
                <select
                  id={IdsGenerator({
                    prefix: "esign popup",
                    id: "select",
                    suffix: "sign type",
                  })}
                data-testid={IdsGenerator({
                    prefix: "esign popup",
                    id: "select",
                    suffix: "sign type",
                  })}
                  className={inputcss.inputFieldVoter}
                  value={signType}
                  onChange={(e) => {
                    setSignType(e.target.value as SignTypeValue);
                  }}
                >
                  {ESIGN_SIGN_TYPE_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
                <div
                  style={{
                    marginTop: "6px",
                    display: "inline-flex",
                    alignItems: "center",
                    gap: "6px",
                    padding: "2px 10px",
                    borderRadius: "999px",
                    fontWeight: 600,
                    fontSize: "12px",
                    background: selectedBadge.background,
                    color: selectedBadge.color,
                  }}
                >
                  {signType === "DIGITAL_ESIGN_EMAIL"
                    ? "✉️"
                    : signType === "DIGITAL_ESIGN_WHATSAPP"
                      ? "💬"
                      : "🔐"}{" "}
                  {selectedBadge.text}
                </div>
              </div>
            </div>

            {signType === "DIGITAL_ESIGN_EMAIL" && (
              <div className="d-flex flex-column mb-4">
                <label className={`${Styles.label}`}>Signer Email:</label>
                <InputField
                  id={IdsGenerator({
                    prefix: "esign popup",
                    id: "input",
                    suffix: "signer email",
                  })}
                  text="lowerCase"
                  onChange={() => isValid("email")}
                  inputRef={signerEmailRef}
                  isError={error.emailError}
                  purpose="forVoter"
                  placeholder="example@domain.com"
                />
                {error.emailError && (
                  <ErrorMessage>
                    Please enter a valid email address (max{" "}
                    {SIGNER_EMAIL_MAX_LENGTH} characters)
                  </ErrorMessage>
                )}
              </div>
            )}

            {signType === "DIGITAL_ESIGN_WHATSAPP" && (
              <div className="d-flex flex-column mb-4">
                <label className={`${Styles.label}`}>Signer Number:</label>
                <PhoneInput
                  country={"in"}
                  value={signerPhoneValue}
                  onChange={(value) => {
                    setSignerPhoneValue(value);
                    isValid("phone", false, value);
                  }}
                  inputProps={{
                    name: "signer_number_wa",
                    autoComplete: "off",
                  }}
                  containerStyle={{ width: "100%" }}
                  inputStyle={{
                    width: "100%",
                    height: "45px",
                    fontSize: "14px",
                    border: error.phoneError
                      ? "1px solid #dc3545"
                      : "1px solid #e2e5e9",
                    borderRadius: "8px",
                  }}
                  buttonStyle={{
                    borderTopLeftRadius: "8px",
                    borderBottomLeftRadius: "8px",
                    borderRight: "1px solid #e2e5e9",
                  }}
                  specialLabel=""
                  enableAreaCodes={false}
                  countryCodeEditable={false}
                />
                {error.phoneError && (
                  <ErrorMessage>
                    Enter a valid phone number (e.g. +919876543210 or 10-digit
                    Indian mobile)
                  </ErrorMessage>
                )}
              </div>
            )}

            {signType === "AADHAAR_ESIGN_OTP" && (
              <div className="d-flex flex-column mb-4">
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "repeat(2, minmax(240px, 1fr))",
                    gap: "12px",
                  }}
                >
                  <div style={{ minWidth: 0 }}>
                    <label className={`${Styles.label}`}>Signer Email:</label>
                    <InputField
                      id={IdsGenerator({
                        prefix: "esign popup",
                        id: "input",
                        suffix: "signer email aadhaar",
                      })}
                      text="lowerCase"
                      onChange={() => isValid("email")}
                      inputRef={signerEmailRef}
                      isError={error.emailError}
                      purpose="forVoter"
                      placeholder="example@domain.com"
                    />
                    {error.emailError && (
                      <ErrorMessage>
                        Please enter a valid email address (max{" "}
                        {SIGNER_EMAIL_MAX_LENGTH} characters)
                      </ErrorMessage>
                    )}
                  </div>
                  <div style={{ minWidth: 0 }}>
                    <label className={`${Styles.label}`}>
                      Signer Number (Optional):
                    </label>
                    <PhoneInput
                      country={"in"}
                      value={signerPhoneValue}
                      onChange={(value) => {
                        setSignerPhoneValue(value);
                        isValid("phone", false, value);
                      }}
                      inputProps={{
                        name: "signer_number_aadhaar",
                        autoComplete: "off",
                      }}
                      containerStyle={{ width: "100%" }}
                      inputStyle={{
                        width: "100%",
                        height: "45px",
                        fontSize: "14px",
                        border: error.phoneError
                          ? "1px solid #dc3545"
                          : "1px solid #e2e5e9",
                        borderRadius: "8px",
                      }}
                      buttonStyle={{
                        borderTopLeftRadius: "8px",
                        borderBottomLeftRadius: "8px",
                        borderRight: "1px solid #e2e5e9",
                      }}
                      specialLabel=""
                      enableAreaCodes={false}
                      countryCodeEditable={false}
                    />
                    {error.phoneError && (
                      <ErrorMessage>
                        Enter a valid phone number (e.g. +919876543210 or
                        10-digit Indian mobile)
                      </ErrorMessage>
                    )}
                  </div>
                </div>
                {error.contactEitherOrError && (
                  <ErrorMessage>
                    Enter at least a signer email or signer number
                  </ErrorMessage>
                )}
              </div>
            )}

            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(2, minmax(240px, 1fr))",
                gap: "12px",
              }}
            >
              <div>
                <label className={`${Styles.label} flex-grow-1`}>
                  Variable X
                </label>
                <input
                  className={inputcss.inputFieldVoter}
                  type="text"
                  id="numberx" data-testid="numberx"
                  value={statex}
                  onChange={(e: any) => {
                    const onlyNumbers = /^[0-9]+$/;
                    if (
                      e.target.value === "" ||
                      onlyNumbers.test(e.target.value)
                    ) {
                      setStatex(e.target.value);
                      isValid("variableX");
                    }
                  }}
                  required
                />
                {error.variableXError && (
                  <ErrorMessage>
                    Variable X must be between {COORDINATE_MIN} and{" "}
                    {COORDINATE_MAX}
                  </ErrorMessage>
                )}
              </div>

              <div>
                <label className={`${Styles.label} flex-grow-1`}>
                  Variable Y
                </label>
                <input
                  className={inputcss.inputFieldVoter}
                  type="text"
                  id="numbery" data-testid="numbery"
                  value={statey}
                  onChange={(e: any) => {
                    const onlyNumbers = /^[0-9]+$/;
                    if (
                      e.target.value === "" ||
                      onlyNumbers.test(e.target.value)
                    ) {
                      setStatey(e.target.value);
                      isValid("variableY");
                    }
                  }}
                  required
                />
                {error.variableYError && (
                  <ErrorMessage>
                    Variable Y must be between {COORDINATE_MIN} and{" "}
                    {COORDINATE_MAX}
                  </ErrorMessage>
                )}
              </div>
            </div>
          </div>
          <div className="pt-3 pl-4 pb-3 mt-2 d-flex flex-column">
            <div className="d-flex align-items-center py-0 gap-2">
              <input {...testIdProps("consent-checkbox-esign")}
                type="checkbox"
                className={`${Styles.formCheckInput} `}
                onChange={(e: any) => {
                  setIsChecked(e.target.checked);
                  isValid();
                }}
              />
              <p className={`${Styles.ackText} mb-0`}>
                I hereby agree, to let zoop.one verify my data for verification
              </p>
            </div>
            {error.consentError && (
              <ErrorMessage>Please provide consent!</ErrorMessage>
            )}
          </div>
          <div className={`${Styles.buttonGroup}`}>
            <StyledButton
              id="id-submit-button-esign"
              hoverStyle={{ ...buttonStyles.primary.hover }}
              disabled={isDisabled}
              isLoading={loading}
              style={{ ...buttonStyles.primary.normal }}
              onClick={() => {
                setTimeout(() => setError({ ...defaultESignErrorState }), 2000);
                handleData();
              }}
            >
              Run Verification
            </StyledButton>
          </div>
        </div>
      </div>
    </div>
  );
}

export default ESignPopup;
